// React
import { useState, useEffect, useMemo, forwardRef } from 'react'

// Mantine
import {
    Combobox,
    TextInput,
    useCombobox,
} from '@mantine/core'

import { useDebouncedValue } from '@mantine/hooks'

// Search
import useSearch from '../../hooks/useSearch'

// Date formatting
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

// Order status labels
import { orderFinancialLabels as financialLabels, orderFulfillmentLabels as fulfillmentLabels } from '../../lib/orderStatuses'

// Configure dayjs
dayjs.extend(relativeTime)

export default function AlgoliaSearch({
    algolia_index,
    onSelect,
    label = null,
    placeholder = 'Rechercher...',
    excludedIds = [],
    filters = null,
}) {
    const { singleSearch, isReady } = useSearch()

    const [query, setQuery] = useState('')
    const [debouncedQuery] = useDebouncedValue(query, 300)

    const [results, setResults] = useState([])

    const search = async () => {
        if (!isReady) return
        const { hits } = await singleSearch(algolia_index, debouncedQuery, {
            hitsPerPage: 200,
            filters: filters ?? undefined,
        })

        setResults(hits)
    }

    const onChange = (value) => {
        if (typeof value !== 'object') {
            setQuery(value)
        }
    }

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })

    // Custom item component
    const CustomItem = forwardRef(function CustomItem(
        { result, ...others },
        ref
    ) {
        let imageUrl = result.image_url

        if (imageUrl && imageUrl.startsWith('https://cdn.shopify.com/')) {
            imageUrl = imageUrl.replace(
                'https://cdn.shopify.com/',
                'https://balticwatches-6242.imgix.net/'
            )

            imageUrl = imageUrl + '&auto=format,compress&w=100'
        }

        return (
            <div className="flex items-center justify-between">
                <div className="flex flex-col highlight-results-em">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: result._highlightResult?.name?.value || result.name,
                        }}
                    />

                    {result.reference && (
                        <span
                            className="text-gray-500 text-xs"
                            dangerouslySetInnerHTML={{
                                __html: result._highlightResult?.reference?.value || result.reference,
                            }}
                        />
                    )}
                </div>

                <div className="text-right h-[50px]">
                    {result.image_url && (
                        <img
                            className="h-[50px] aspect-square object-contain"
                            src={imageUrl}
                            alt={result.name}
                        />
                    )}
                </div>

                {!result.image_url && (
                    <div className="flex flex-col items-end gap-0.5">
                        {(result.order_financial_status || result.order_fulfillment_status) && (
                            <span className="text-[11px] flex items-center gap-1">
                                {financialLabels[result.order_financial_status] && (
                                    <span className={financialLabels[result.order_financial_status].color}>
                                        {financialLabels[result.order_financial_status].label}
                                    </span>
                                )}
                                {financialLabels[result.order_financial_status] && fulfillmentLabels[result.order_fulfillment_status] && (
                                    <span className="text-gray-300">·</span>
                                )}
                                {fulfillmentLabels[result.order_fulfillment_status] && (
                                    <span className={fulfillmentLabels[result.order_fulfillment_status].color}>
                                        {fulfillmentLabels[result.order_fulfillment_status].label}
                                    </span>
                                )}
                            </span>
                        )}

                        {result.created_at && (
                            <span className="text-gray-400 text-[11px]">
                                {dayjs.unix(result.created_at).fromNow()}
                            </span>
                        )}
                    </div>
                )}
            </div>
        )
    })

    // Search on query change
    useEffect(() => {
        if (debouncedQuery && isReady) {
            search()
        } else {
            setResults([])
        }
    }, [debouncedQuery, isReady])

    // Combobox options

    const resultsById = useMemo(
        () => Object.fromEntries(results.map((r) => [String(r.id), r])),
        [results]
    )

    const excludedSet = useMemo(
        () => new Set(excludedIds.map(String)),
        [excludedIds]
    )

    const options = results
        .filter((result) => !excludedSet.has(String(result.id)))
        .map((result) => (
            <Combobox.Option value={String(result.id)} key={result.id}>
                <CustomItem result={result} />
            </Combobox.Option>
        ))

    return (
        <>
            <Combobox
                store={combobox}
                onOptionSubmit={(selectedId) => {
                    const result = resultsById[selectedId]
                    if (result) {
                        onSelect(result)
                    }
                    setQuery('')
                    combobox.closeDropdown()
                }}
                styles={{
                    'data-combobox-active': {
                        backgroundColor: 'red',
                    },
                }}
            >
                <Combobox.Target>
                    <TextInput
                        label={label}
                        placeholder={placeholder}
                        value={query}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        onClick={() => combobox.openDropdown()}
                        onFocus={() => combobox.openDropdown()}
                    />
                </Combobox.Target>

                {options.length > 0 && (
                    <Combobox.Dropdown mah={400} style={{ overflowY: 'auto' }}>
                        <Combobox.Options>{options}</Combobox.Options>
                    </Combobox.Dropdown>
                )}
            </Combobox>
        </>
    )
}
